import { FC, useState } from "react";
import { Segmented } from "antd";
import { MdAirlineSeatReclineNormal, MdLuggage } from "react-icons/md";
import { TbToolsKitchen2 } from "react-icons/tb";
import i18n from "src/i18n";
import { AirplaneIcon } from "src/assets/Icons";
import { Timeline } from "src/components";
import { formatNumber } from "src/utility";
import type { PaymentChipRow } from "../../PaymentChip/PaymentChip";
import {
  toDisplayItems,
  AggregatedBaggage,
  AggregatedMeal,
  AggregatedSeat,
  SSRFlight,
  TabKey,
  TransformedSegment,
} from "../utils";

// ─── Constants ────────────────────────────────────────────────────────────────

const MODAL_TABS: { label: string; icon: React.ReactNode; key: TabKey }[] = [
  { get label() { return i18n.t("Flights.details.ssr.seats"); }, icon: <MdAirlineSeatReclineNormal className="h-full w-full" />, key: "seats" },
  { get label() { return i18n.t("Flights.details.ssr.baggage"); }, icon: <MdLuggage className="h-full w-full" />, key: "baggages" },
  { get label() { return i18n.t("Flights.details.ssr.meals"); }, icon: <TbToolsKitchen2 className="h-full w-full" />, key: "meals" },
];

const ModalSegmentSelector: FC<{
  segments: TransformedSegment[];
  onChange: (val: number) => void;
  value: number;
}> = ({
  segments,
  onChange,
  value,
}) => {
  if (segments.length <= 1) return null;
  const options = segments.map((seg, i) => ({
    label: seg.direction === "Outbound" ? i18n.t("Flights.search.departure") : i18n.t("Flights.search.return"),
    value: i + 1,
  }));
  return <Segmented options={options} onChange={onChange} value={value} />;
};

const ModalTabs: FC<{ value: TabKey; onChange: (key: TabKey) => void }> = ({ value, onChange }) => {
  return (
    <div className="flex w-full border-b border-border-default">
      {MODAL_TABS.map((tab) => (
        <div
          role="button"
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`p-4! flex flex-1 cursor-pointer items-center justify-center gap-2 border-b-2 py-2 text-sm font-medium transition-colors
            ${value === tab.key ? "border-border-active text-font-active" : "border-transparent text-font-minimal hover:text-font-subtle"}`}
        >
          <div className="w-5">{tab.icon}</div>
          <div>{tab.label}</div>
        </div>
      ))}
    </div>
  );
};

const FlightSSRSection: FC<{
  ssrFlight: SSRFlight<AggregatedMeal | AggregatedBaggage | AggregatedSeat>;
  tab: TabKey;
}> = ({
  ssrFlight,
  tab,
}) => {
  const origin = ssrFlight.flight.origin.cityName || ssrFlight.flight.origin.code;
  const destination = ssrFlight.flight.destination.cityName || ssrFlight.flight.destination.code;
  const items = toDisplayItems(tab, ssrFlight);

  return (
    <div className="flex flex-col gap-4 rounded-md bg-background-default p-3">
      <div className="text-font-default flex items-center justify-between gap-2 text-sm font-semibold">
        <div className="text-font-informative flex flex-1 items-center justify-center rounded-sm bg-background-informative px-1 py-2">{origin}</div>
        <div className="flex-3">
          <Timeline
            items={[<></>, <></>]}
            connectorOverlays={[<AirplaneIcon className="h-5 w-5 text-icon-secondary" />]}
            className="flex-1!"
            classNames={{
              connector: {
                rail: "h-px border-dashed border-t border-border-default!",
                head: "w-0! h-0!",
                overlay: "bg-background-default! -translate-y-1/2!",
              },
            }}
          />
        </div>
        <div className="text-font-informative flex flex-1 items-center justify-center rounded-sm bg-background-informative px-1 py-2">{destination}</div>
      </div>
      <div className="flex flex-col gap-4">
        {items.map((item, i) => (
          <div key={i} className="flex items-start justify-between gap-2">
            <div className="text-font-subtle flex flex-col">
              {item.label}
              {item.quantity > 1 && <span className="ms-1">x{item.quantity}</span>}
            </div>
            <div className="text-font-default ms-4 shrink-0 font-semibold">
              {formatNumber(item.price * item.quantity)} {item.currency}
              {item.quantity > 1 && (
                <div className="ms-1 text-xs font-normal text-font-minimal">
                  ({formatNumber(item.price)} x{item.quantity})
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SSRBreakdownContent: FC<{ segments: TransformedSegment[]; addOnRows: PaymentChipRow[] }> = ({ segments, addOnRows }) => {
  const [selectedSegment, setSelectedSegment] = useState(1);
  const [selectedTab, setSelectedTab] = useState<TabKey>("seats");
  const activeSegment = segments[selectedSegment - 1];
  const ssrFlights: SSRFlight<AggregatedMeal | AggregatedBaggage | AggregatedSeat>[] = activeSegment?.[selectedTab] ?? [];
  const grandTotal = addOnRows.reduce((sum, row) => sum + row.total, 0);
  const currency = addOnRows[0]?.currency ?? "";

  return (
    <div className="flex flex-col gap-4 p-4">
      <ModalSegmentSelector segments={segments} onChange={setSelectedSegment} value={selectedSegment} />
      <ModalTabs value={selectedTab} onChange={setSelectedTab} />
      <div className="bg-background-subtle flex flex-col gap-4 p-5">
        {ssrFlights.length === 0 ? (
          <div className="py-4 text-center text-sm text-font-minimal">{i18n.t("Flights.details.summary.noAddOnSelectedForSegment", { type: selectedTab })}</div>
        ) : (
          ssrFlights.map((ssrFlight, i) => <FlightSSRSection key={i} ssrFlight={ssrFlight} tab={selectedTab} />)
        )}
      </div>
      <div className="flex items-center justify-between">
        <div className="text-font-subtle text-lg font-semibold">{i18n.t("Flights.details.summary.totalValue")}</div>
        <div className="text-font-default font-semibold">
          {formatNumber(grandTotal)} {currency}
        </div>
      </div>
    </div>
  );
};

export default SSRBreakdownContent;

export type { PaymentChipRow };
